import React, { useMemo, useState } from 'react';
import { useToast } from './Toast';
import { useAuth } from '../AuthContext';
import { useEntityList } from '../data/entities';
import { addComment, actorOf } from '../data/deals';

// Comment thread for any anchor ({ kind: 'deal'|'doc'|'clause'|'dealPoint', … }).
// Comments live in one per-deal collection; anchors are matched client-side
// (deal-scale volumes — revisit if a deal ever exceeds thousands of comments).
export default function CommentThread({ scope, participant, anchor, compact = false }) {
  const { user } = useAuth();
  const toast = useToast();
  const { docs: all } = useEntityList('comment', scope);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const anchorKey = JSON.stringify(anchor);
  const comments = useMemo(() => {
    const target = JSON.parse(anchorKey);
    return (all || [])
      .filter((c) => {
        const a = c.anchor || {};
        return (
          a.kind === target.kind &&
          (a.docId || '') === (target.docId || '') &&
          (a.clauseId || '') === (target.clauseId || '') &&
          (a.dealPointId || '') === (target.dealPointId || '')
        );
      })
      .sort((a, b) => a.createdAt - b.createdAt);
  }, [all, anchorKey]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      const mentions = [...body.matchAll(/@([\w.@+-]+)/g)].map((m) => m[1]);
      await addComment(scope, actorOf(user, participant), anchor, body.trim(), mentions);
      setBody('');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
      {!compact && <strong className="u-small">Comments</strong>}
      {all === null && <div className="cs-skeleton" style={{ height: 32 }} />}
      {all && !comments.length && <span className="u-xs u-muted">No comments yet.</span>}
      {comments.map((c) => (
        <div key={c.id} className="u-small">
          <strong>{c.authorName}</strong>
          {c.partyKey && <span className="u-muted"> ({c.partyKey})</span>}
          <span className="u-muted u-xs u-tabular"> · {new Date(c.createdAt).toLocaleString()}</span>
          <div style={{ whiteSpace: 'pre-wrap' }}>{c.body}</div>
        </div>
      ))}
      <form className="u-cluster" style={{ '--gap': 'var(--sp-2)' }} onSubmit={submit}>
        <input
          className="cs-input"
          style={{ flex: 1, minWidth: 180 }}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Comment — @mention teammates"
          aria-label="Add comment"
        />
        <button type="submit" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy || !body.trim()}>
          Post
        </button>
      </form>
    </div>
  );
}
